import React, { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { AuthenticatedOutletContext } from "../components/AuthenticatedAppShell";
import Library from "../components/Library";
import {
  APP_ROUTES,
  librarySectionPath,
} from "../routing/appRoutes";

interface LibraryRouteState {
  notice?: "reading-unavailable";
}

const LibraryRoute: React.FC = () => {
  const { sectionId } = useParams<{ sectionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, libraryResetSignal, debugModeEnabled } =
    useOutletContext<AuthenticatedOutletContext>();
  const routeSectionId = sectionId ?? null;
  const routeState = location.state as LibraryRouteState | null;
  const hasUnavailableNoticeInRouteState =
    location.pathname === APP_ROUTES.library &&
    routeState?.notice === "reading-unavailable";
  const [capturedUnavailableNotice, setCapturedUnavailableNotice] = useState(
    hasUnavailableNoticeInRouteState,
  );

  useEffect(() => {
    if (!hasUnavailableNoticeInRouteState) return;

    setCapturedUnavailableNotice(true);
    navigate(
      location.pathname + location.search + location.hash,
      { replace: true, state: null },
    );
  }, [
    hasUnavailableNoticeInRouteState,
    location.hash,
    location.pathname,
    location.search,
    navigate,
  ]);

  const unavailableNotice =
    location.pathname === APP_ROUTES.library && capturedUnavailableNotice;

  return (
    <Library
      userId={user.id}
      targetTier={user.targetTier}
      routeSectionId={routeSectionId}
      resetToLibrarySignal={libraryResetSignal}
      debugModeEnabled={debugModeEnabled}
      unavailableNotice={unavailableNotice}
      onRequestSection={(id) => navigate(librarySectionPath(id))}
      onExitReader={() => navigate(APP_ROUTES.library, { replace: true })}
      onRouteSectionUnavailable={(id) => {
        if (id !== routeSectionId) return;
        navigate(APP_ROUTES.library, {
          replace: true,
          state: {
            notice: "reading-unavailable",
          } satisfies LibraryRouteState,
        });
      }}
    />
  );
};

export default LibraryRoute;
